import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

import type { EsnStatus } from '../../../shared/types/atoms';
import { IconButtonComponent } from '../../atoms/icon-button/icon-button.component';
import { StatusBadgeComponent } from '../../atoms/status-badge/status-badge.component';
import { IslandThumbComponent } from '../../molecules/island-thumb/island-thumb.component';

/**
 * One island in the islands grid: its biome, name, slug, lifecycle state and version on a pane of
 * glass. The whole card opens the island; the corner trigger (or a projected `[esnIslandCardActions]`
 * menu) offers the island's actions without opening it.
 */
@Component({
  selector: 'esn-island-card',
  imports: [IconButtonComponent, StatusBadgeComponent, IslandThumbComponent],
  templateUrl: './island-card.component.html',
  styleUrl: './island-card.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IslandCardComponent {
  readonly name = input.required<string>();
  readonly slug = input.required<string>();

  /**
   * Biome thumbnail URL.
   */
  readonly imageUrl = input.required<string>();

  readonly status = input<EsnStatus>('published');

  /**
   * Visible text of the status badge, e.g. "Published".
   */
  readonly statusLabel = input.required<string>();

  /**
   * Version caption, e.g. "Version 1.0.0".
   */
  readonly version = input<string | null>(null);

  /**
   * Accessible name of the corner actions trigger.
   */
  readonly actionsLabel = input('Island actions');

  /**
   * The card itself was activated: open the island.
   */
  readonly opened = output<void>();

  /**
   * The default corner trigger was clicked; the event lets the page anchor its menu.
   */
  readonly actions = output<MouseEvent>();
}
